import { writeWorkspace } from '../db/workspace-write'
import type { FlashcardsFeRouterCtx } from './flashcards-fe.router-ctx'
import type { PageRef } from '@w/content-blocks/schema'
import type { Blocks } from '@w/content-blocks'
import { newPageDocument } from '../db/page-document'
import { commitPageEdit, movePage } from '../db/commit-page-edit'

export type MoveInput = { source: PageRef; destination: { spaceId: string; parentId: string | null } }

/** A teamspace root, else the caller's personal root from the workspace's directory entry. */
function rootPage(ctx: FlashcardsFeRouterCtx, workspaceId: string, teamspaceId?: string) {
  return teamspaceId ?? ctx.spaces.get(workspaceId).space.personalRootId
}

/** Placement is a replicated child edge written with the page, offline included. */
export async function createPageCommand(ctx: FlashcardsFeRouterCtx, input: {
  workspaceId: string; pageId: string; parentPageId?: string; teamspaceId?: string
  title: string; blocks: Blocks; translatable?: true; readonly?: true
}) {
  await writeWorkspace(ctx, input.workspaceId, async () => {
    const parentPageId = input.parentPageId ?? rootPage(ctx, input.workspaceId, input.teamspaceId)
    const db = ctx.db(input.workspaceId)
    const doc = newPageDocument({ title: input.title, content: { kind: 'note', children: input.blocks },
      deletedAt: null, ...(input.translatable ? { translatable: true } : {}) }, ctx.auth.getUser().id)
    await db.transaction(async tx => {
      const existing = await tx.query`SELECT ${tx.pages.id} FROM ${tx.pages}
        WHERE ${tx.pages.id.eq(input.pageId)}`.executeOne({ id: tx.pages.id })
      if (existing) return
      await commitPageEdit(tx, { parentId: parentPageId, pageId: input.pageId,
        userId: ctx.auth.getUser().id, frame: doc.encodeSnapshot().bytes,
        fields: { title: input.title, translatable: input.translatable, readonly: input.readonly, deletedAt: null, content: { kind: 'note', children: input.blocks } }, now: new Date(), tel: ctx.tel, bucket: ctx.bucket })
  })
  })
  return { workspaceId: input.workspaceId, pageId: input.pageId }
}

/** Moves delete the observed child edge and insert its replacement; server checks Full + destination Edit. */
export async function movePageCommand(ctx: FlashcardsFeRouterCtx, input: MoveInput): Promise<void> {
  await writeWorkspace(ctx, input.source.spaceId, async () => {
    const workspaceId = ctx.spaces.get(input.source.spaceId).space.workspaceId
    if (ctx.spaces.get(input.destination.spaceId).space.workspaceId !== workspaceId) throw new Error('Unable to move page')
    const parentId = input.destination.parentId ?? rootPage(ctx, workspaceId)
    await ctx.writes.flush(input.source.spaceId)
    const db = ctx.db(workspaceId)
    await movePage(db, { pageId: input.source.pageId, parentPageId: parentId, userId: ctx.auth.getUser().id, now: new Date(), tel: ctx.tel })
  })
}

/** Adoption owns document content, including the created page's placement; moves are page edits. */
export function adoptPageCommands(ctx: FlashcardsFeRouterCtx, workspaceId: string) {
  return {
    get personalRootId() { return ctx.spaces.get(workspaceId).space.personalRootId },
    move(input: { pageId: string; parentPageId?: string }) {
      return movePageCommand(ctx, { source: { spaceId: workspaceId, pageId: input.pageId }, destination: { spaceId: workspaceId, parentId: input.parentPageId ?? null } })
    },
  }
}
